import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { formatDateTime } from '@/lib/i18n';
import { api } from '@/lib/api';
import { useCurrency } from '@/Hooks/useCurrency';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { EmptyState, LoadingState } from '@/components/ui/states';
import {
  CUSTOMER_360_HISTORY_LIMIT,
  apiError,
  mergeCounts,
  previewConsentReconciliation,
  type ChannelConsent,
  type Customer360,
  type CustomerMergeRecord,
} from '../lib';

/**
 * Merge a duplicate into `winner`.
 *
 * ADR-0010 §2 calls this "the sharp edge": it moves a loyalty balance and
 * repoints reservations, feedback, coupons and tags between two rows, in one
 * transaction, and it is only reversible until the loser's snapshot is
 * redacted by an erasure or the winner spends the moved points. There is no
 * `POST /segments/preview`-style dry-run endpoint for a merge, so this dialog
 * builds its own preview from both customers' `/360` payloads before it lets
 * anyone confirm — search, then a side-by-side of exactly what moves, then a
 * second click.
 */
export default function MergeDialog({
  winnerId,
  winnerName,
  onClose,
  onMerged,
}: {
  winnerId: string;
  winnerName: string | null;
  onClose: () => void;
  onMerged: () => void;
}) {
  const { t } = useTranslation();
  const [step, setStep] = useState<'search' | 'preview'>('search');
  const [search, setSearch] = useState('');
  const [candidates, setCandidates] = useState<Array<{ id: string; name: string | null; phone: string | null }>>([]);
  const [searching, setSearching] = useState(false);
  const [loserId, setLoserId] = useState<string | null>(null);

  useEffect(() => {
    if (!search.trim() || step !== 'search') {
      setCandidates([]);
      return;
    }
    setSearching(true);
    const timer = setTimeout(() => {
      api
        .get<{ data: Array<{ id: string; name: string | null; phone: string | null }> }>('/customers', {
          params: { search: search.trim(), limit: 10 },
        })
        .then(({ data }) => setCandidates((data.data ?? []).filter((c) => c.id !== winnerId)))
        .catch(() => setCandidates([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [search, step, winnerId]);

  if (step === 'search') {
    return (
      <Dialog
        open
        onClose={onClose}
        title={t('crm.merge.title')}
        description={t('crm.merge.searchHint', { name: winnerName ?? t('crm.customers.unnamed') })}
        className="max-w-lg"
        footer={
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        }
      >
        <div className="space-y-3">
          <Field label={t('crm.merge.searchLabel')}>
            <Input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('crm.customers.searchPlaceholder')}
            />
          </Field>
          {searching && <LoadingState label={t('common.loading')} />}
          {!searching && search.trim() && candidates.length === 0 && (
            <EmptyState title={t('crm.merge.noMatches')} />
          )}
          {candidates.length > 0 && (
            <ul className="divide-y rounded-lg border">
              {candidates.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setLoserId(c.id);
                      setStep('preview');
                    }}
                    className="flex w-full items-center justify-between gap-2 p-3 text-start text-sm hover:bg-hover"
                  >
                    <span className="font-medium">{c.name ?? t('crm.customers.unnamed')}</span>
                    <span dir="ltr" className="text-ink-muted">{c.phone ?? '—'}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Dialog>
    );
  }

  return (
    <MergePreview
      winnerId={winnerId}
      loserId={loserId!}
      onBack={() => setStep('search')}
      onClose={onClose}
      onMerged={onMerged}
    />
  );
}

function MergePreview({
  winnerId,
  loserId,
  onBack,
  onClose,
  onMerged,
}: {
  winnerId: string;
  loserId: string;
  onBack: () => void;
  onClose: () => void;
  onMerged: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { format: formatMoney } = useCurrency(null);
  const [winner, setWinner] = useState<Customer360 | null>(null);
  const [loser, setLoser] = useState<Customer360 | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get<Customer360>(`/customers/${winnerId}/360`),
      api.get<Customer360>(`/customers/${loserId}/360`),
    ])
      .then(([w, l]) => {
        setWinner(w.data);
        setLoser(l.data);
      })
      .catch((e) => setError(apiError(e, t('crm.merge.previewFailed'))))
      .finally(() => setLoading(false));
  }, [winnerId, loserId, t]);

  const confirm = async () => {
    setConfirming(true);
    try {
      const { data } = await api.post<CustomerMergeRecord>(`/customers/${winnerId}/merge`, {
        loserId,
        reason: reason.trim() || undefined,
      });
      const counts = mergeCounts(data);
      toast.success(
        t('crm.merge.done', {
          points: data.pointsMoved,
          reservations: counts.reservations,
          feedback: counts.feedback,
          coupons: counts.coupons,
        }),
      );
      onMerged();
    } catch (e) {
      toast.error(apiError(e, t('crm.merge.failed')));
    } finally {
      setConfirming(false);
    }
  };

  const capAt = (n: number) => (n >= CUSTOMER_360_HISTORY_LIMIT ? `${n}+` : String(n));

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('crm.merge.previewTitle')}
      className="max-w-2xl"
      footer={
        <>
          <Button variant="outline" onClick={onBack}>
            {t('common.back')}
          </Button>
          <Button variant="danger" onClick={() => void confirm()} disabled={loading || !!error || confirming}>
            {t('crm.merge.confirm')}
          </Button>
        </>
      }
    >
      {loading ? (
        <LoadingState label={t('common.loading')} />
      ) : error || !winner || !loser ? (
        <EmptyState title={error ?? t('crm.merge.previewFailed')} />
      ) : (
        <div className="space-y-4">
          <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/50 dark:text-amber-200">
            {t('crm.merge.irreversibleHint')}
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-lg border p-3">
              <p className="text-xs uppercase tracking-wide text-ink-muted">{t('crm.merge.survivor')}</p>
              <p className="font-semibold">{winner.customer.name ?? t('crm.customers.unnamed')}</p>
              <p dir="ltr" className="text-ink-muted">{winner.customer.phone ?? '—'}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs uppercase tracking-wide text-ink-muted">{t('crm.merge.duplicate')}</p>
              <p className="font-semibold">{loser.customer.name ?? t('crm.customers.unnamed')}</p>
              <p dir="ltr" className="text-ink-muted">{loser.customer.phone ?? '—'}</p>
            </div>
          </div>

          <div className="space-y-2 rounded-lg border p-3">
            <h4 className="text-sm font-semibold">{t('crm.merge.whatMoves')}</h4>
            <dl className="grid grid-cols-2 gap-y-1.5 text-sm sm:grid-cols-3">
              <MoveRow
                label={t('crm.merge.loyaltyPoints')}
                value={(loser.loyalty?.points ?? 0) > 0 ? `+${loser.loyalty?.points}` : '0'}
              />
              <MoveRow label={t('crm.merge.reservations')} value={capAt(loser.reservations.length)} />
              <MoveRow label={t('crm.merge.feedback')} value={capAt(loser.feedback.length)} />
              <MoveRow label={t('crm.merge.coupons')} value={capAt(loser.coupons.length)} />
              <MoveRow
                label={t('crm.merge.tags')}
                value={String(loser.tags.filter((tag) => !winner.tags.some((w) => w.id === tag.id)).length)}
              />
            </dl>
            {(loser.loyalty?.points ?? 0) > 0 && (
              <p className="text-xs text-ink-muted">
                {t('crm.merge.loyaltyDetail', {
                  points: loser.loyalty?.points,
                  from: loser.customer.name ?? t('crm.customers.unnamed'),
                  to: winner.customer.name ?? t('crm.customers.unnamed'),
                })}
              </p>
            )}
          </div>

          <div className="space-y-2 rounded-lg border p-3">
            <h4 className="text-sm font-semibold">{t('crm.merge.consentAfter')}</h4>
            <p className="text-xs text-ink-muted">{t('crm.merge.consentRule')}</p>
            <ul className="divide-y text-sm">
              {winner.consent.map((wc) => {
                const lc = loser.consent.find((c) => c.channel === wc.channel) as ChannelConsent;
                const result = previewConsentReconciliation(wc, lc);
                return (
                  <li key={wc.channel} className="flex items-center justify-between gap-2 py-1.5">
                    <span>{t(`crm.consent.channel.${wc.channel}`)}</span>
                    <span className="flex items-center gap-1.5 text-xs text-ink-muted">
                      <StatusChip status={wc.status} />
                      <span>+</span>
                      <StatusChip status={lc.status} />
                      <span>→</span>
                      <StatusChip status={result} strong />
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>

          <Field label={t('crm.merge.reason')} hint={t('crm.merge.reasonHint')}>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>

          <p className="text-xs text-ink-muted">
            {t('crm.merge.spendNote', {
              spend: formatMoney(loser.stats.totalSpend),
              visits: loser.stats.visits,
              date: loser.stats.lastVisitAt ? formatDateTime(loser.stats.lastVisitAt, i18n.language) : '—',
            })}
          </p>
        </div>
      )}
    </Dialog>
  );
}

function MoveRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-ink-muted">{label}</dt>
      <dd className="tabular-nums font-medium">{value}</dd>
    </div>
  );
}

function StatusChip({ status, strong }: { status: string | null; strong?: boolean }) {
  const { t } = useTranslation();
  return (
    <Badge tone={status === 'GRANTED' ? 'success' : status ? 'danger' : 'neutral'} className={strong ? 'font-semibold' : undefined}>
      {status ? t(`crm.consent.status.${status}`) : t('crm.consent.unknown')}
    </Badge>
  );
}
